import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineArrowBack } from 'react-icons/md';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import GaugeComponent from 'react-gauge-component';
import { FaPerson } from 'react-icons/fa6';
import moment from 'moment';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Button from 'components/Button2';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';

import './index.css';

const timePerQuestionRW = 64 * 60 / 54;
const timePerQuestionMath = 70 * 60 / 44;
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];

type DigitalSATReportProps = {
    // Bulk print mounts this component directly instead of routing to it, so
    // the test id has to be able to arrive as a prop as well as a URL param.
    testIdOverride?: string;
    // Rendered inside the bulk-print container: drop the page chrome (document
    // title, action buttons) so the sheet carries report content only.
    embedded?: boolean;
    // Fires once this report's data has settled, so a bulk print can wait for
    // every report to arrive before opening the print dialog.
    onReady?: () => void;
};

function DigitalSATReport({ testIdOverride, embedded = false, onReady }: DigitalSATReportProps = {}) {
    const [testData, setTestData] = useState(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [rootCategoryName, setRootCategoryName] = useState(null);

    const { user } = useAuthContext();
    const windowSize = useWindowSize();
    const navigate = useNavigate();
    const { testId: routeTestId } = useParams();
    const testId = testIdOverride ?? routeTestId;
    const printRef = useRef(null);

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    const getOrdinalSuffix = (number: number) => {
        if (number > 3 && number < 21) {
            return 'th';
        }
        switch (number % 10) {
            case 1:
                return 'st';
            case 2:
                return 'nd';
            case 3:
                return 'rd';
            default:
                return 'th';
        }
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        // Marks the print iframe's <body> so the question tables are revealed
        // for this single-report print only. Bulk print never sets it.
        bodyClass: 'printing-single-report',
    });

    // `onReady` is read through a ref so a parent passing a fresh closure each
    // render cannot retrigger the fetch -- the effect stays keyed on the id.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        if (!testId) return;
        let cancelled = false;
        axios.get(`tests/digital/satResultTutor/${testId}/`)
            .then(res => {
                if (cancelled) return;
                setTestData(res.data);
                onReadyRef.current?.();
            })
            .catch(error => {
                console.log(error);
                // Report ready even on failure: one unreachable report must not
                // leave a bulk print waiting for a dialog that never opens.
                if (!cancelled) onReadyRef.current?.();
            });

        const handleClickOutside = (event) => {
            if (event.target.className !== 'testReport__levelLabel' && event.target.className !== 'testReport__levelLabel--active') {
                setRootCategoryName(null);
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            cancelled = true;
            document.removeEventListener('click', handleClickOutside);
        }
    }, [testId]);

    if (!testData) return <></>;

    return (
        <div className='testReport' ref={printRef}>
            {!embedded &&
                <Helmet>
                    <title>
                        Test Report - Test Center Admin
                    </title>
                </Helmet>
            }
            {!embedded && (
                <div className='testReport__bar'>
                    <button className='testReport__back' onClick={() => navigate(-1)}>
                        <MdOutlineArrowBack className='testReport__backIcon' />
                        Back
                    </button>
                </div>
            )}
            <div className='testReport__headerGroup'>
                <div className='testReport__headerSection'>
                    {testData.logo && 
                        <img
                            className='testReport__headerLogo'
                            alt='logo'
                            src={testData.logo}
                        />
                    }
                </div>
                <div className='testReport__headerSection'>
                    <div className='testReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Email: {testData.email}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='testReport__headerGroup'>
                <h1 className='testReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='testReport__buttonGroup'>
                    <ButtonLink className='testReport__button--blue' to={`/review/digital-sat/${testId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                    <Button className='testReport__button' secondary onClick={handlePrint}>
                        PRINT REPORT
                    </Button>
                    <ButtonLink className='testReport__button' to={`/students/${testData.studentId}/digital-sat/performanceHistory`}>
                        PERFORMANCE HISTORY
                    </ButtonLink>
                </div>
            </div>
            <div className='testReport__card'>
                <div className='testReport__cardRow'>
                    <div className='testReport__scoresGroup'>
                        <h2 className='testReport__scoreTitle'>TOTAL SCORE</h2>
                        <ScoreWithScale
                            className='testReport__scoreWithScale'
                            score={Math.floor(testData.score)}
                            scoreMin={Math.floor(testData.minScore)}
                            scoreMax={Math.floor(testData.maxScore)}
                            fontSize={70}
                            // fontSize={windowSize.width > 576
                            //     ? 100
                            //     : 70
                            // }
                        />
                        {user.customizations.find(customization => customization === 'Show Percentile') &&
                            <p className='testReport__scorePercentile'>
                                <b className='testReport__scorePercentileNumber'>
                                    {testData.percentile}
                                    <span>{getOrdinalSuffix(testData.percentile)}</span>
                                </b>
                                <FaPerson className='testReport__scorePercentileIcon' />
                                <span className='testReport__scorePercentileText'>
                                    SAT User Percentile
                                </span>
                            </p>
                        }
                        <div className='testReport__scoreDivider'></div>
                        <h2 className='testReport__scoreTitle'>SECTION SCORES</h2>
                        {testData.testSections.map((testSection, t) => (<>
                            <h3 className='testReport__sectionScoreTitle'>
                                {testSection.subject}
                            </h3>
                            <ScoreWithScale
                                className='testReport__scoreWithScale'
                                score={Math.floor(testSection.score)}
                                scoreMin={Math.floor(testSection.minScore)}
                                scoreMax={Math.floor(testSection.maxScore)}
                            />
                            {user.customizations.find(customization => customization === 'Show Percentile') &&
                                <p className='testReport__scorePercentile'>
                                    <b className='testReport__scorePercentileNumber'>
                                        {testSection.percentile}
                                        <span>{getOrdinalSuffix(testSection.percentile)}</span>
                                    </b>
                                    <FaPerson className='testReport__scorePercentileIcon' />
                                    <span className='testReport__scorePercentileText'>
                                        SAT User Percentile
                                    </span>
                                </p>
                            }
                            {t < testData.testSections.length - 1 &&
                                <div className='testReport__scoreDivider'></div>
                            }
                        </>))}
                    </div>
                    <div className='testReport__overview'>
                        <h2 className='testReport__cardSubTitle'>
                            Raw Scores
                        </h2>
                        <div className='testReport__overviewRow'>
                            {testData.testSections.map(testSection => (
                                <div className='testReport__overviewItem'>
                                    <h4 className='testReport__overviewLabel'>
                                        {testSection.subject}
                                    </h4>
                                    <table className='testReport__overviewTable'>
                                        <tbody>
                                            <tr>
                                                <th></th>
                                                <th>Correct</th>
                                                <th>Time</th>
                                            </tr>
                                            {testData.testModules.filter(testModule => (
                                                testModule.subject === testSection.subject
                                            )).map((testModule, t) => (
                                                <tr>
                                                    <td>
                                                        Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                                                    </td>
                                                    <td>{testModule.answers.filter(answer => answer.isCorrect).length}/{testModule.answers.length}</td>
                                                    <td>{formatTime(testModule.answers.reduce((total, answer) => (
                                                        total + answer.timeSpent
                                                    ), 0))}</td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td>Total</td>
                                                <td>{testData.testModules.filter(testModule => (
                                                    testModule.subject === testSection.subject
                                                )).reduce((total, testModule) => (
                                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                                ), 0)}/{testData.testModules.filter(testModule => (
                                                    testModule.subject === testSection.subject
                                                )).reduce((total, testModule) => (
                                                    total + testModule.answers.length
                                                ), 0)}</td>
                                                <td>{formatTime(testData.testModules.filter(testModule => (
                                                    testModule.subject === testSection.subject
                                                )).reduce((total, testModule) => (
                                                    total + testModule.answers.reduce((time, answer) => (
                                                        time + answer.timeSpent
                                                    ), 0)
                                                ), 0))}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                        <h2 className='testReport__cardSubTitle'>
                            Category Scores
                        </h2>
                        <div className='testReport__overviewRow'>
                            {testData.testSections.map((testSection, s) => (
                                <div className='testReport__overviewItem'>
                                    <h4 className='testReport__overviewLabel'>
                                        {testSection.subject}
                                    </h4>
                                    {testSection.rootCategories.map(rootCategory => (<>
                                        <h5
                                            className={rootCategoryName === rootCategory.name
                                                ? 'testReport__levelLabel--active'
                                                : 'testReport__levelLabel'
                                            }
                                            onClick={() => setRootCategoryName(rootCategory.name)}
                                        >
                                            {rootCategory.name}
                                            <div className={rootCategoryName === rootCategory.name
                                                ? 'testReport__levelDetailed--active'
                                                : 'testReport__levelDetailed'
                                            }>
                                                {rootCategory.categories.map(category => (
                                                    <div className='testReport__levelDetailedItem'>
                                                        <p>{category.name}</p>
                                                        <p className={category.numCorrect/category.numQuestions >= 0.8
                                                            ? 'testReport__levelDetailedGreen'
                                                            : category.numCorrect/category.numQuestions >= 0.4
                                                            ? 'testReport__levelDetailedYellow'
                                                            : 'testReport__levelDetailedRed'
                                                        }>
                                                            {category.numCorrect}/{category.numQuestions}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </h5>
                                        <LevelBar
                                            className={s % 2 === 0 ? 'testReport__levelGreen' : 'testReport__levelOrange'}
                                            percent={100*rootCategory.numCorrect/rootCategory.numQuestions}
                                            label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                        />
                                    </>))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <h2 className='testReport__cardSubTitle--centered'>
                    Section Time Averages
                </h2>
                <div className='testReport__overviewRow'>
                    {testData.testSections.map(testSection => (
                        <div className='testReport__overviewItem'>
                            <h4 className='testReport__overviewLabel--centered'>
                                {testSection.subject}
                            </h4>
                            <table className='testReport__timeTable'>
                                <tbody>
                                    <tr>
                                        <th></th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {testSection.rootCategories.map(rootCategory => (
                                        <tr>
                                            <td>
                                                {rootCategory.name}
                                            </td>
                                            <td>
                                                {formatTime2(rootCategory.timeSpent)}
                                            </td>
                                            <td>
                                                <GaugeComponent
                                                    className='testReport__timeGauge'
                                                    type='semicircle'
                                                    value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                    minValue={0}
                                                    maxValue={testSection.subject === 'Reading and Writing'
                                                        ?   timePerQuestionRW * 3
                                                        :   testSection.subject === 'Math'
                                                        ?   timePerQuestionMath * 3
                                                        :   180
                                                    }
                                                    arc={{
                                                        'subArcs': [
                                                            { length: 0.33, color: "#5BE12C"},
                                                            { length: 0.33, color: "#F5CD19"},
                                                            { length: 0.33, color: "#EA4228"}
                                                        ]
                                                    }}
                                                    // style={{ 'width': '200px' }}
                                                    pointer={{
                                                        'width': 10,
                                                    }}
                                                    labels={{
                                                        'valueLabel': {
                                                            // 'formatTextValue': (value) => formatTime(value),
                                                            // 'style': {
                                                            //     'fontSize': '40px',
                                                            //     'fill': '#000',
                                                            //     'textShadow': null,
                                                            //     'marginTop': 20
                                                            // }
                                                            'hide': true
                                                        },
                                                        'tickLabels': {
                                                            'hideMinMax': true
                                                        }
                                                    }}
                                                />
                                                <p>{formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>
            {/* <div className='testReport__header'>
                <div className='testReport__headerRow'>
                    <div className='testReport__headerRowInfo'>
                        <h1 className='testReport__title'>
                            {testData.name}
                        </h1>
                        <p className='testReport__headerText'>
                            {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                    <h2 className='testReport__studentName'>
                        {testData.studentName}
                    </h2>  
                </div>
                <div className='testReport__logo'>
                    {testData.logo&&<img src={testData.logo} alt='logo' className='testReport__logoContent'/>}
                </div>
            </div>  
            <h2 className='testReport__subHeader'>
                Test result overview
            </h2>
            <div className='testReport__scores'>
                {testData.score && <>
                    <div className='testReport__scoreColumn'>
                        <h3 className='testReport__scoreMainTitle'>
                            Your Total Score
                        </h3>
                        <ScoreWithScale
                            score={Math.floor(testData.score)}
                            scoreMin={Math.floor(testData.minScore)}
                            scoreMax={Math.floor(testData.maxScore)}
                            fontSize={windowSize.width > 576
                                ? 100
                                : 70
                            }
                        />
                    </div>
                    <div className='testReport__scoreGroup'>
                        {testData.testSections.map(testSection => (
                            <div className='testReport__scoreColumn' key={testData.name + testSection.subject}>
                                <h3 className='testReport__scoreSubTitle'>
                                    Your {testSection.subject} Score
                                </h3>
                                <ScoreWithScale
                                    score={Math.floor(testSection.score)}
                                    scoreMin={Math.floor(testSection.minScore)}
                                    scoreMax={Math.floor(testSection.maxScore)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className='testReport__buttonGroup'>
                        <Button
                            className='testReport__button'
                            secondary
                            onClick={handlePrint}
                        >
                            PRINT REPORT
                        </Button>
                        <ButtonLink
                            className='testReport__button'
                            to={`/review/${testId}`}
                        >
                            REVIEW QUESTIONS
                        </ButtonLink>
                    </div>
                </>}
            </div>
            <div className='testReport__scoresPrint'>
                {testData.score && <>
                    <div className='testReport__scoreColumn'>
                        <h3 className='testReport__scoreMainTitle'>
                            Your Total Score
                        </h3>
                        <ScoreWithScale
                            score={Math.floor(testData.score)}
                            scoreMin={Math.floor(testData.minScore)}
                            scoreMax={Math.floor(testData.maxScore)}
                            fontSize={50}
                        />
                    </div>
                    <div className='testReport__scoreGroup'>
                        {testData.testSections.map(testSection => (
                            <div className='testReport__scoreColumn' key={testData.name + testSection.subject}>
                                <h3 className='testReport__scoreSubTitle'>
                                    Your {testSection.subject} Score
                                </h3>
                                <ScoreWithScale
                                    score={Math.floor(testSection.score)}
                                    scoreMin={Math.floor(testSection.minScore)}
                                    scoreMax={Math.floor(testSection.maxScore)}
                                    fontSize={30}
                                />
                            </div>
                        ))}
                    </div>
                    <div className='testReport__buttonGroup'>
                        <Button
                            className='testReport__button'
                            secondary
                            onClick={handlePrint}
                        >
                            PRINT REPORT
                        </Button>
                        <ButtonLink
                            className='testReport__button'
                            to={`/review/${testId}`}
                        >
                            REVIEW QUESTIONS
                        </ButtonLink>
                    </div>
                </>}
            </div>
            <div className='testReport__rootCategoriesWrapper'>
                {testData.testSections.map((testSection, t) => {
                    const maxNumQuestions = Math.max(...testSection.rootCategories.map(category => category.numQuestions));
                    if (testSection.rootCategories.length > 0) {
                        return (
                            <div className='testReport__rootCategories' key={'root' + testSection.subject}>
                                <h3 className='testReport__weakAreasTitle'>
                                    {testSection.subject} Section Overview
                                </h3>
                                <table className='testReport__rootCategoriesTable'>
                                    <tbody>
                                        <tr>
                                            <th>{testSection.subject}</th>
                                            <th></th>
                                            <th></th>
                                        </tr>
                                        {testSection.rootCategories.sort((a, b) => a.name > b.name ? 1 : -1).map(category => (
                                            <tr key={category.name}>
                                                <td>{category.name}</td>
                                                <td>
                                                    <div className='testReport__rootCategoriesGraph'>
                                                        <div style={{
                                                            'width': `${Math.max(1, 100*category.numCorrect/maxNumQuestions)}%`
                                                        }}></div>
                                                        <div style={{
                                                            'width': `${100*category.numQuestions/maxNumQuestions}%`
                                                        }}></div>
                                                    </div>
                                                </td>
                                                <td>{category.numCorrect}/{category.numQuestions} Correct</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    }
                })}
            </div>
            {testData.testSections.map((testSection, t) => {
                const weakCategories = testSection.categories.filter(category => (
                    category.numCorrect < category.numQuestions
                )).sort((a, b) => (a.numCorrect/a.numQuestions === b.numCorrect/b.numQuestions
                    ? b.numQuestions - a.numQuestions
                    : a.numCorrect/a.numQuestions - b.numCorrect/b.numQuestions
                ));

                const topWeakCategories = weakCategories.length > 5 ? weakCategories.slice(0, 5) : weakCategories;

                const maxNumQuestions = Math.max(...topWeakCategories.map(category => category.numQuestions));
                
                if (topWeakCategories.length > 0) {
                    return (
                        <div className='testReport__weakAreas' key={'weak' + testSection.subject}>
                            <h3 className='testReport__weakAreasTitle'>
                                Your Focus Areas in {testSection.subject}
                            </h3>
                            <table className='testReport__weakAreasTable'>
                                <tbody>
                                    <tr>
                                        <th>{testSection.subject}</th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                    {topWeakCategories.map(category => (
                                        <tr key={category.name}>
                                            <td>{category.name}</td>
                                            <td>
                                                <div className='testReport__weakAreasGraph'>
                                                    <div style={{
                                                        'width': `${Math.max(1, 100*category.numCorrect/maxNumQuestions)}%`
                                                    }}></div>
                                                    <div style={{
                                                        'width': `${100*category.numQuestions/maxNumQuestions}%`
                                                    }}></div>
                                                </div>
                                            </td>
                                            <td>{category.numCorrect}/{category.numQuestions} Correct</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );
                }
                else return <></>;
            })} */}
            <div className='testReport__printBreak'></div>
            {/* <h2 className='testReport__subHeader'>
                Test result detailed breakdown
            </h2> */}
            <div className='testReport__questions'>
                <h2 className='testReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='testReport__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'testReport__sectionTab--active'
                                : 'testReport__sectionTab'
                            }
                            onClick={() => {
                                setSectionIndex(t);
                                setModuleIndex(0);
                            }}
                            key={testSection.subject}
                        >
                            {testSection.subject}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'testReport__sectionTabIndicatorLeft'
                        : 'testReport__sectionTabIndicatorRight'
                    }></div>
                </div>
                {testData.testModules.filter(testModule => (
                    testModule.subject === testData.testSections[sectionIndex].subject
                )).length > 1 &&
                    <div className='testReport__moduleTabs'>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testData.testSections[sectionIndex].subject
                        )).map((testModule, t) => (
                            <button
                                className={moduleIndex === t
                                    ? 'testReport__moduleTab--active'
                                    : 'testReport__moduleTab'
                                }
                                onClick={() => setModuleIndex(t)}
                                key={testModule.name}
                            >
                                Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                            </button>
                        ))}
                        <div className={moduleIndex === 0
                            ? 'testReport__moduleTabIndicatorLeft'
                            : 'testReport__moduleTabIndicatorRight'
                        }></div>
                    </div>
                }
                
                <div className='testReport__questionsOverview'>
                    <div className='testReport__questionsSummary'>
                        <div className='testReport__questionsSummaryItem'>
                            <p className='testReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.length
                                ), 0)}
                            </p>
                            <p className='testReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='testReport__questionsSummaryItem'>
                            <p className='testReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='testReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='testReport__questionsSummaryItem'>
                            <p className='testReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => !answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='testReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    {/* {testData.testModules.filter(testModule => (
                        testModule.subject === testData.testSections[sectionIndex].subject
                    )).map(testModule => <>
                        <p className='testReport__moduleNumber'>Module {testModule.moduleNumber}</p>
                        <table className='testReport__questionsTable'>
                            <tbody>
                                <tr>
                                    <th>Question</th>
                                    <th>Correct Answer</th>
                                    <th>Your Answer</th>
                                </tr>
                                    {testModule.answers.map(answer => (
                                        <tr key={testModule.name + answer.questionNumber}>
                                            <td>{answer.questionNumber}</td>
                                            <td>{answer.correctChoice !== null
                                                ?   MULTIPLE_CHOICES[answer.correctChoice]
                                                :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                            }</td>
                                            <td>{answer.answer !== null
                                                ?   (answer.isCorrect
                                                        ? <p>{answer.answer}; Correct</p>
                                                        : <p className='testReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                                    )
                                                :   answer.choice !== null
                                                ?   (answer.choice === answer.correctChoice
                                                        ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                        : <p className='testReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                                    )
                                                :   <p className='testReport__questionOmitted'>Omitted</p>
                                            }</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </>)} */}
                    <table className='testReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Your Answer</th>
                                <th>Category</th>
                                {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                    <th>Correct %</th>
                                }
                                <th>Time Spent</th>
                            </tr>
                            {testData.testModules.filter(testModule => (
                                testModule.subject === testData.testSections[sectionIndex].subject
                            ))[moduleIndex].answers.map(answer => (
                                <tr key={testData.testModules[moduleIndex].name + answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    <td>{answer.correctChoice !== null
                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                    }</td>
                                    <td>{answer.answer !== null
                                        ?   (answer.isCorrect
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='testReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='testReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='testReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>{answer.category}</td>
                                    {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                        <td>{answer.correctRate ? `${parseFloat(answer.correctRate).toFixed(1)}%` : '-'}</td>
                                    }
                                    <td>
                                        <p className={answer.timeSpent > (testData.testModules[moduleIndex].subject === 'Reading and Writing'
                                                    ? timePerQuestionRW * 1.5
                                                    : testData.testModules[moduleIndex].subject === 'Math'
                                                    ? timePerQuestionMath * 1.5
                                                    : 60 * 1.5
                                                )
                                            ?   'testReport__timeSpentBad'
                                            :   answer.timeSpent > (testData.testModules[moduleIndex].subject === 'Reading and Writing'
                                                    ? timePerQuestionRW
                                                    : testData.testModules[moduleIndex].subject === 'Math'
                                                    ? timePerQuestionMath
                                                    : 60
                                                )
                                            ?   'testReport__timeSpentWarning'
                                            :   'testReport__timeSpentGood'
                                        }>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className='testReport__categoryPrint'>
                {testData.testSections.map(testSection => <div className='testReport__categoryPrintSection'>
                    <h3 className='testReport__categoryPrintSubjectName'>{testSection.subject}</h3>
                    <div className='testReport__categoryPrintSubject'>
                        {testSection.rootCategories.map(rootCategory => <div className='testReport__categoryPrintRootGroup'>
                            <h4 className='testReport__categoryPrintRoot'>{rootCategory.name}</h4>
                            {rootCategory.categories.map(category => (
                                <div className='testReport__levelDetailedItem'>
                                    <p>{category.name}</p>
                                    <p className={category.numCorrect/category.numQuestions >= 0.8
                                        ? 'testReport__levelDetailedGreen'
                                        : category.numCorrect/category.numQuestions >= 0.4
                                        ? 'testReport__levelDetailedYellow'
                                        : 'testReport__levelDetailedRed'
                                    }>
                                        {category.numCorrect}/{category.numQuestions}
                                    </p>
                                </div>
                            ))}
                        </div>)}
                    </div>
                </div>)}
            </div>
            {/*
                Print-only: every module, one sheet per section with both of its
                modules stacked. No heading and no Correct % column: both cost
                width or height a full section cannot spare, and the module
                labels say what each table is. Hidden in print by default and revealed only
                for a single-report print (see the
                `printing-single-report` rules in index.css), so a bulk print
                stays overview-only.
            */}
            <div className='testReport__questionsPrint'>
                {testData.testSections.map(testSection => (
                    <div className='testReport__questionsPrintSection' key={`print:${testSection.subject}`}>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testSection.subject
                        )).map(testModule => (
                            <div className='testReport__questionsPrintModule' key={`print:${testModule.name}`}>
                                <p className='testReport__moduleName'>
                                    {testModule.name} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                                </p>
                                <table className='testReport__questionsTable'>
                                    <thead>
                                        <tr>
                                            <th>Question</th>
                                            <th>Correct Answer</th>
                                            <th>Your Answer</th>
                                            <th>Category</th>
                                            <th>Time Spent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {testModule.answers.map(answer => (
                                            <tr key={testModule.name + answer.questionNumber}>
                                                <td>{answer.questionNumber}</td>
                                                <td>{answer.correctChoice !== null
                                                    ?   MULTIPLE_CHOICES[answer.correctChoice]
                                                    :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                                }</td>
                                                <td>{answer.answer !== null
                                                    ?   (answer.isCorrect
                                                            ? <p>{answer.answer}; Correct</p>
                                                            : <p className='testReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                                        )
                                                    :   answer.choice !== null
                                                    ?   (answer.choice === answer.correctChoice
                                                            ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                            : <p className='testReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                                        )
                                                    :   <p className='testReport__questionOmitted'>Omitted</p>
                                                }</td>
                                                <td>{answer.category}</td>
                                                <td>
                                                    <p className={answer.timeSpent > (testModule.subject === 'Reading and Writing'
                                                                ? timePerQuestionRW * 1.5
                                                                : testModule.subject === 'Math'
                                                                ? timePerQuestionMath * 1.5
                                                                : 60 * 1.5
                                                            )
                                                        ?   'testReport__timeSpentBad'
                                                        :   answer.timeSpent > (testModule.subject === 'Reading and Writing'
                                                                ? timePerQuestionRW
                                                                : testModule.subject === 'Math'
                                                                ? timePerQuestionMath
                                                                : 60
                                                            )
                                                        ?   'testReport__timeSpentWarning'
                                                        :   'testReport__timeSpentGood'
                                                    }>
                                                        {formatTime(answer.timeSpent)}
                                                    </p>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default DigitalSATReport;